const MAX_DATE_EPOCH_MS = 8_640_000_000_000_000;

function epochMs(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Math.trunc(numeric) : 0;
  }
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizedKeyPart(value, maximumLength) {
  const normalized = String(value || "").trim();
  return normalized && normalized.length <= maximumLength ? normalized : "";
}

function observedHighWater(io, domain, itemId) {
  const normalizedDomain = normalizedKeyPart(domain, 40);
  const normalizedItemId = normalizedKeyPart(itemId, 4_096);
  if (!normalizedDomain || !normalizedItemId) return 0;
  return Math.max(0, epochMs(io?.read?.(normalizedDomain, normalizedItemId)));
}

/**
 * A local mutation made after observing a remote row is causally newer even
 * when this device's wall clock is slow. Preserve that ordering explicitly.
 */
export function nextRowMutationEpochMs(observedUpdatedAt, nowEpochMs = Date.now()) {
  const observed = Math.max(0, epochMs(observedUpdatedAt));
  const now = Math.max(1, epochMs(nowEpochMs) || Date.now());
  return Math.min(MAX_DATE_EPOCH_MS, Math.max(now, observed + 1));
}

export function nextRowMutationIso(observedUpdatedAt, nowEpochMs = Date.now()) {
  return new Date(nextRowMutationEpochMs(observedUpdatedAt, nowEpochMs)).toISOString();
}

export function observeRowMutationHighWater(io, domain, itemId, ...versions) {
  const normalizedDomain = normalizedKeyPart(domain, 40);
  const normalizedItemId = normalizedKeyPart(itemId, 4_096);
  if (!normalizedDomain || !normalizedItemId) return 0;
  const current = observedHighWater(io, normalizedDomain, normalizedItemId);
  const next = versions.reduce(
    (maximum, value) => Math.max(maximum, Math.max(0, epochMs(value))),
    current,
  );
  if (next > current) {
    io?.write?.(normalizedDomain, normalizedItemId, String(next));
  }
  return next;
}

/**
 * Advance and durably record the next local version. The persisted high-water
 * survives a row being removed by a remote tombstone and a subsequent reload.
 */
export function nextDurableRowMutationIso(
  io,
  domain,
  itemId,
  observedUpdatedAt,
  nowEpochMs = Date.now(),
) {
  const observed = observeRowMutationHighWater(
    io,
    domain,
    itemId,
    observedUpdatedAt,
  );
  const next = nextRowMutationEpochMs(observed, nowEpochMs);
  observeRowMutationHighWater(io, domain, itemId, next);
  return new Date(next).toISOString();
}
